import { aliasOf, property, subclass } from '@arcgis/core/core/accessorSupport/decorators';

import { messageBundle, storeNode, tsx } from '@arcgis/core/widgets/support/widget';

import Widget from '@arcgis/core/widgets/Widget';

import ContentAreaViewModel from './ContentArea/ContentAreaViewModel';

import "./ContentArea/styles/ContentArea.scss";

import AppTypeTile, { AppTypeTileProperties } from "../widgets/AppTypeTile";

import { watch, whenDefinedOnce, whenTrueOnce } from '@arcgis/core/core/watchUtils';
import AppTile from '../widgets/AppTile';
import NavBarViewModel, { navbarRoute } from './NavBar/NavBarViewModel';
import { VNode } from 'src/interfaces/interfaces';
import Handles from '@arcgis/core/core/Handles';

import ContentAreaT9N from "../../t9n/ui/ContentArea/ContentArea.json";
import NavBarT9N from "../../t9n/ui/NavBar/NavBar.json";
import AppTypeInfoViewModel from '../data/AppTypeInfo/AppTypeInfoViewModel';

import Telemetry from "@esri/telemetry";

export interface ContentAreaProperties extends __esri.WidgetProperties {
  name?: string;
  view?: __esri.MapView | __esri.SceneView;
  telemetry: Telemetry;
}


@subclass('Landing.ui.ContentArea')
export default class ContentArea extends Widget {

  @aliasOf('viewModel.view')
  view: __esri.MapView | __esri.SceneView;

  @property()
  @messageBundle("dist/assets/t9n/ui/ContentArea/ContentArea")
  messages: typeof ContentAreaT9N;

  @property()
  @aliasOf("viewModel.telemetry")
	telemetry: Telemetry;

  @property()
  @messageBundle("dist/assets/t9n/ui/NavBar/NavBar")
  navbarMessages: typeof NavBarT9N;

  @property()
  @aliasOf("viewModel.appMessages")
  @messageBundle("dist/assets/t9n/App/App")
  appMessages;

  @property()
  apps: AppTypeTileProperties[];

  @property()
  @aliasOf("viewModel.portal")
  portal: __esri.Portal;

  /** Original data arrays */
  @property()
  browseAllTiles: AppTypeTile[];
  @property()
  featuredTiles: AppTypeTile[];
  @property()
  myAppsTiles: AppTile[];

  /** Views are arrays that have been sorted and filtered. 
   * The references to the Tiles are consistent to original data arrays (Tiles aren't copied) */
  @property()
  browseAllTilesView: AppTypeTile[];
  @property()
  featuredTilesView: AppTypeTile[];
  @property()
  myAppsTilesView: AppTile[];

  @property()
  @aliasOf("viewModel.navbarVM")
  navbarVM: NavBarViewModel;

  @property({
    type: ContentAreaViewModel
  })
  viewModel: ContentAreaViewModel = new ContentAreaViewModel();

  private _contentArea: HTMLDivElement;
  private _contentOverlay: HTMLDivElement;
  private _handles: Handles = new Handles();

  constructor(properties?: ContentAreaProperties) {
    super(properties);
  }

  postInitialize() {
    const { viewModel } = this;
    const { appTypeInfoVM } = viewModel;

    whenTrueOnce(appTypeInfoVM, "isAppTypeDataLoaded", () => {
      if (this.viewModel.itemLoaderVM.loadedItemType === "none") {
        this._initialAppTemplateSetup(appTypeInfoVM);
      } else {
        whenDefinedOnce(viewModel, "notCompatible", () => {
          this._initialAppTemplateSetup(appTypeInfoVM);
        });
      }
    });

    this._handles.add(watch(this.viewModel.myAppsDataVM, "myApps", (myApps: __esri.Collection<__esri.PortalItem>) => {
      this.myAppsTiles = this._generateMyAppTiles(myApps, this.viewModel);
      if (this.navbarVM.navigationRoute === "myApps") {
        this._generateViewForAppTiles(this.myAppsTiles, "myAppsTilesView");
      }
    }));

    this._handles.add(this.navbarVM.watch("navigationRoute", ()=>{
      this._resetScrollViewToTop();
    }));

    this._handles.add(this.navbarVM.watch(["sortField", "selectedFilterTag", "navigationRoute"], () => {
      switch (this.navbarVM.navigationRoute) {
        case "browseAll":
          this._generateViewForAppTypeTiles(this.browseAllTiles, "browseAllTilesView");
          return;
        case "featured":
          this._generateViewForAppTypeTiles(this.featuredTiles, "featuredTilesView");
          return;
        case "myApps":
          this._generateViewForAppTiles(this.myAppsTiles, "myAppsTilesView")
          this._assertPaginationValidity();
          return;
      }
    }));
  }

  render() {
    const tiles = this._renderNavBarRoute();
    return (
      <div
        class="app-layout__content-area"
        bind={this}
        afterCreate={storeNode}
        data-node-ref={"_contentArea"}
      >
        <div id="map-area" afterCreate={this.viewModel.createMap.bind(this.viewModel)}></div>
        <div
          bind={this}
          afterCreate={storeNode}
          data-node-ref={"_contentOverlay"}
          class={this.classes(`content-overlay`, `${this.navbarVM.navigationRoute === "myApps" ? "content-overlay--myApps" : ""}`)}>
          {
            tiles?.length === 0
              ? this._renderNoResults(this.navbarVM.navigationRoute)
              : this._renderTileArea(tiles)
          }
        </div>
        <div>
          {this._renderAlertNotification()}
        </div>
      </div>
    );
  }

  destroy() {
    this._handles.removeAll();
  }

  setHeight(height: number): void {
    this._contentArea.style.height = `${height}px`;
  }

  private _initialAppTemplateSetup(appTypeInfoVM: AppTypeInfoViewModel) {
    // find searchSuggestions from AppType Item's Tags
    this.navbarVM.setSearchSelectionWithTags(appTypeInfoVM.appTypeInfo);

    // calculate browse all tab apps
    const browseAllApps = appTypeInfoVM.appTypeInfo
      .map((item) => { return this.viewModel.formatAppTypeItems(item); });
    this.browseAllTiles = this._generateAppTypeTiles(browseAllApps);
    this._generateViewForAppTypeTiles(this.browseAllTiles, "browseAllTilesView");


    // calculate featured tab apps
    const featuredApps = appTypeInfoVM.appTypeInfo
      .filter((item: __esri.PortalItem) => { return this.viewModel.filterFeaturedApps(item); })
      .map((item) => { return this.viewModel.formatAppTypeItems(item) });

    this.featuredTiles = this._generateAppTypeTiles(featuredApps);
    this._generateViewForAppTypeTiles(this.featuredTiles, "featuredTilesView");


    this.scheduleRender();
  }

  /** Preforms sort and filter on AppTypeTiles, and stores result in a dataview */
  private _generateViewForAppTypeTiles(data: AppTypeTile[], viewField: string): void {
    let viewArray: AppTypeTile[] = this.viewModel.createView(data) as AppTypeTile[];

    const sortField = this.navbarVM.sortField;
    if (sortField) {
      this.navbarVM.sortTiles(sortField, viewArray);
    }

    const filterTag = this.navbarVM.selectedFilterTag;
    if (filterTag && filterTag !== "") {
      viewArray = viewArray.filter((tile: AppTypeTile) => { return tile.appTypeInfo.portalItem.tags.findIndex(tag => tag === filterTag) !== -1 });
    }

    this[viewField] = viewArray;
  }

  /** Preforms sort and filter on AppTiles, and stores result in a dataview */
  private _generateViewForAppTiles(data: AppTile[], viewField: string): void {
    let viewArray: AppTile[] = this.viewModel.createView(data) as AppTile[];

    const sortField = this.navbarVM.sortField;
    if (sortField) {
      this.navbarVM.sortTiles(sortField, viewArray);
    }

    const filterTag = this.navbarVM.selectedFilterTag.toLocaleLowerCase();
    if (filterTag && filterTag !== "") {
      viewArray = viewArray.filter((tile: AppTile) => { return tile.appInfo.title.toLocaleLowerCase().indexOf(filterTag) !== -1 });
    }

    this[viewField] = viewArray;
  }

  private _renderNavBarRoute(): VNode[] {
    switch (this.navbarVM.navigationRoute) {
      case "browseAll":
        return this.browseAllTilesView?.map(tile => tile?.render());
      case "featured":
        return this.featuredTilesView?.map(tile => tile?.render());
      case "myApps":
        return this.myAppsTilesView?.map(tile => tile?.render());
    }
  }

  private _start: number = 1;
  private _num: number = 20;

  private _renderTileArea(tiles: VNode[]): VNode {
    switch (this.navbarVM.navigationRoute) {
      case "browseAll":
        return (
          <div key="tiles" class="tiles">
            {
              ...tiles
            }
          </div>
        );
      case "featured":
        return (
          <div key="tiles" class="tiles">
            {
              ...tiles
            }
          </div>
        );
      case "myApps":
        return ([
          <div key="tiles-myApps" class="tiles-myApps">
            <div key="tiles" class="tiles">
              {
                ...tiles?.slice(
                  this._start - 1,
                  (this._start - 1) + this._num
                )
              }
            </div>
          </div>,
          this?.myAppsTiles?.length > this._num ? <calcite-pagination
            key="tiles-pagination"
            class="pagination-footer"
            start={this._start}
            num={this._num}
            total={tiles?.length}
            afterCreate={(paginationCtrl: HTMLCalcitePaginationElement) => {
              paginationCtrl.addEventListener("calcitePaginationChange", (e: any) => {
                this._start = e.detail.start;
                this._resetScrollViewToTop();
                this.scheduleRender();
              });
            }}
          >
          </calcite-pagination> : null
        ]);
    }
  }

  /** 
   * If the MyApps view contains less apps than the start index of the 
   * current page, then this function changes the current page to fit inside the current view */
  private _assertPaginationValidity(){
    const myAppsViewCount = this?.myAppsTilesView?.length;
    if(myAppsViewCount == null || myAppsViewCount === 0) { return; }
    let currentPageStartValue = this._start;
    while(myAppsViewCount < currentPageStartValue){
      currentPageStartValue -= this._num;
    }
    this._start = currentPageStartValue;
    this.scheduleRender();
  }

  private _generateAppTypeTiles(appTypes: AppTypeTileProperties[]): AppTypeTile[] {
    return appTypes.map((prop) => {
      return new AppTypeTile(prop);
    });
  }

  private _generateMyAppTiles(myApps: __esri.Collection<__esri.PortalItem>, contentAreaVM: ContentAreaViewModel): AppTile[] {
    return myApps.map((appInfo) => {
      return new AppTile({ appInfo, portal: this.portal, contentAreaVM });
    }).toArray();
  }

  private _renderNoResults(tab: navbarRoute): VNode {
    const {
      noResults,
      noResultsDescPart1,
      noResultsDescPart2,
      noResultsDescPart3,
      noMyApps_Title,
      noMyApps_Desc
    } = this.appMessages.searchResults;

    if (tab === "browseAll" || tab === "featured") {
      const {
        BrowseAll,
        Featured
      } = this.navbarMessages;
      return (
        <div class="no-results">
          <calcite-card
            class="no-results__card">
            <h3 class="no-results__card-title" slot="title">
              {noResults}
            </h3>
            <span class="no-results__card-content" slot="subtitle">
              {noResultsDescPart1} {tab === "browseAll" ? BrowseAll : Featured} {noResultsDescPart2}.
              &nbsp; {noResultsDescPart3}
            </span>
          </calcite-card>
        </div>
      )
    } else if (tab === "myApps") {
      const {
        MyApps
      } = this.navbarMessages;
      return (
        <div class={this.classes("no-results", "no-results--myApps")}>
          <calcite-card
            class="no-results__card">
            <h3 class="no-results__card-title" slot="title">
              {this?.myAppsTiles?.length === 0 ? noMyApps_Title : noResults}
            </h3>
            <span class="no-results__card-content" slot="subtitle">
              {
                this?.myAppsTiles?.length === 0 ? noMyApps_Desc :
                  `${noResultsDescPart1} ${MyApps} ${noResultsDescPart2}. ${noResultsDescPart3}`
              }
            </span>
          </calcite-card>
        </div>
      )
    }
  }

  private _renderAlertNotification() : VNode[]{
    return this.viewModel.activeAlerts?.map(alert => alert?.render()).toArray();
  }

  private _resetScrollViewToTop(){
    this._contentOverlay.scrollTop = 0;
  }

}